import React, { useState, useEffect } from 'react';
import api, { generateCV } from '../../api/client';
import { Plus, Trash2, Edit3, Link, Copy, CheckCircle, AlertCircle, Users, ExternalLink, Upload, Video, X, FileText, RefreshCw, Loader2 } from 'lucide-react';

function Notification({ message, type, onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const colors = type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800';
    return (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border-l-4 shadow-lg ${colors} flex items-center gap-2`}>
            {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message}
        </div>
    );
}

export default function RecruitersPage() {
    const [recruiters, setRecruiters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notif, setNotif] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [copiedId, setCopiedId] = useState(null);
    const [uploadingVideo, setUploadingVideo] = useState(null);
    const [uploadingPdf, setUploadingPdf] = useState(null);
    const [generatingCv, setGeneratingCv] = useState(false);
    const videoInputRef = React.useRef(null);
    const pdfInputRef = React.useRef(null);

    const fetchRecruiters = () => {
        api.get('/recruiters').then(({ data }) => setRecruiters(data)).catch(() => { }).finally(() => setLoading(false));
    };

    useEffect(() => { fetchRecruiters(); }, []);

    const blankForm = {
        recruiter_name: '',
        recruiter_role: '',
        company_name: '',
        company_description: '',
        company_industry: '',
        company_url: '',
        job_title: '',
        job_description: '',
        custom_pitch: '',
        generate_cv: false,
    };

    const openNew = () => { setForm({ ...blankForm }); setEditing('new'); };

    const openEdit = (r) => {
        setForm({
            recruiter_name: r.recruiter_name || '',
            recruiter_role: r.recruiter_role || '',
            company_name: r.company_name || '',
            company_description: r.company_description || '',
            company_industry: r.company_industry || '',
            company_url: r.company_url || '',
            job_title: r.job_title || '',
            job_description: r.job_description || '',
            custom_pitch: r.custom_pitch || '',
        });
        setEditing(r.id);
    };

    const handleSave = async () => {
        try {
            if (editing === 'new') {
                await api.post('/recruiters', form);
                setNotif({ type: 'success', message: 'Recruteur créé ! Lien personnalisé généré.' });
            } else {
                await api.put(`/recruiters/${editing}`, form);
                setNotif({ type: 'success', message: 'Recruteur mis à jour !' });
            }
            setEditing(null);
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce recruteur ?')) return;
        try {
            await api.delete(`/recruiters/${id}`);
            setNotif({ type: 'success', message: 'Recruteur supprimé' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur de suppression' });
        }
    };

    const copyLink = (slug) => {
        const url = `${window.location.origin}/r/${slug}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedId(slug);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const handleVideoUpload = async (recruiterId, file) => {
        if (!file) return;
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            setNotif({ type: 'error', message: 'Vidéo trop volumineuse (max 100 Mo)' });
            return;
        }
        setUploadingVideo(recruiterId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/recruiters/${recruiterId}/intro-video`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setNotif({ type: 'success', message: 'Vidéo d\'introduction uploadée !' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur lors de l\'upload' });
        } finally {
            setUploadingVideo(null);
        }
    };

    const handleDeleteVideo = async (recruiterId) => {
        if (!window.confirm('Supprimer la vidéo d\'introduction ?')) return;
        try {
            await api.delete(`/recruiters/${recruiterId}/intro-video`);
            setNotif({ type: 'success', message: 'Vidéo supprimée' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur de suppression' });
        }
    };

    const handlePdfUpload = async (recruiterId, file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') {
            setNotif({ type: 'error', message: 'Seuls les fichiers PDF sont acceptés' });
            return;
        }
        if (file.size > 30 * 1024 * 1024) {
            setNotif({ type: 'error', message: 'Fichier trop volumineux (max 30 Mo)' });
            return;
        }
        setUploadingPdf(recruiterId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/recruiters/${recruiterId}/attached-pdf`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setNotif({ type: 'success', message: 'PDF uploadé !' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur lors de l\'upload' });
        } finally {
            setUploadingPdf(null);
        }
    };

    const handleDeletePdf = async (recruiterId) => {
        if (!window.confirm('Supprimer le PDF attaché ?')) return;
        try {
            await api.delete(`/recruiters/${recruiterId}/attached-pdf`);
            setNotif({ type: 'success', message: 'PDF supprimé' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur de suppression' });
        }
    };

    const handleGenerateCv = async (recruiterId) => {
        const existing = recruiters.find(r => r.id === recruiterId)?.attached_pdf_url;
        if (existing && !window.confirm('Cela remplacera le CV/PDF actuel. Continuer ?')) return;
        setGeneratingCv(true);
        try {
            await generateCV(recruiterId);
            setNotif({ type: 'success', message: 'CV adapté généré avec succès !' });
            fetchRecruiters();
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur lors de la génération du CV' });
        } finally {
            setGeneratingCv(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return 'Jamais';
        return new Date(iso).toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    return (
        <div>
            {notif && <Notification {...notif} onClose={() => setNotif(null)} />}

            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Recruteurs</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Gérez vos recruteurs et générez des liens personnalisés
                    </p>
                </div>
                <button onClick={openNew}
                    className="flex items-center gap-2 bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    <Plus className="w-4 h-4" /> Nouveau recruteur
                </button>
            </div>

            {/* Form */}
            {editing !== null && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {editing === 'new' ? 'Nouveau recruteur' : 'Modifier le recruteur'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du recruteur *</label>
                            <input value={form.recruiter_name} onChange={(e) => setForm({ ...form, recruiter_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="Marie Dupont" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle du recruteur</label>
                            <input value={form.recruiter_role} onChange={(e) => setForm({ ...form, recruiter_role: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="DRH, Tech Lead, CTO..." />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                            <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="TechCorp" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Secteur d'activité</label>
                            <input value={form.company_industry} onChange={(e) => setForm({ ...form, company_industry: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="Tech, Finance, Santé..." />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Poste proposé</label>
                            <input value={form.job_title} onChange={(e) => {
                                const val = e.target.value;
                                setForm(prev => ({ ...prev, job_title: val, ...(editing === 'new' && val ? { generate_cv: true } : {}) }));
                            }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="Lead Developer Python" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site web entreprise</label>
                            <input value={form.company_url} onChange={(e) => setForm({ ...form, company_url: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                                placeholder="https://techcorp.com" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description de l'entreprise</label>
                        <textarea value={form.company_description} onChange={(e) => setForm({ ...form, company_description: e.target.value })} rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Quelques mots sur l'entreprise..." />
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description du poste</label>
                        <textarea value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })} rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Descriptif du poste, missions, stack technique..." />
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pitch personnalisé</label>
                        <textarea value={form.custom_pitch} onChange={(e) => setForm({ ...form, custom_pitch: e.target.value })} rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Message personnalisé pour ce recruteur..." />
                    </div>

                    {/* Generate CV checkbox — only for new recruiters */}
                    {editing === 'new' && (
                        <div className="mt-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.generate_cv}
                                    onChange={(e) => setForm({ ...form, generate_cv: e.target.checked })}
                                    className="w-4 h-4 text-[#5764F4] border-gray-300 rounded focus:ring-[#5764F4]"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Générer un CV adapté au poste</span>
                                    <p className="text-xs text-gray-400">Le CV sera généré automatiquement à partir de votre profil, adapté au poste avec l'IA</p>
                                </div>
                            </label>
                        </div>
                    )}

                    <div className="mt-6 flex gap-3">
                        <button onClick={handleSave} className="bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors">
                            Sauvegarder
                        </button>
                        <button onClick={() => setEditing(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors">
                            Annuler
                        </button>
                    </div>

                    {/* Video upload — only for existing recruiters */}
                    {editing !== 'new' && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Vidéo d'introduction</label>
                            {recruiters.find(r => r.id === editing)?.intro_video_url ? (
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm">
                                        <Video className="w-4 h-4" />
                                        Vidéo uploadée
                                    </div>
                                    <button onClick={() => handleDeleteVideo(editing)}
                                        className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 transition-colors">
                                        <X className="w-3.5 h-3.5" /> Supprimer
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <input ref={videoInputRef} type="file" accept="video/*" className="hidden"
                                        onChange={(e) => handleVideoUpload(editing, e.target.files[0])} />
                                    <button onClick={() => videoInputRef.current?.click()}
                                        disabled={uploadingVideo === editing}
                                        className="flex items-center gap-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        <Upload className="w-4 h-4" />
                                        {uploadingVideo === editing ? 'Upload en cours...' : 'Uploader une vidéo'}
                                    </button>
                                    <p className="text-xs text-gray-400 mt-1">Format: MP4, WebM, MOV — Max 100 Mo</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* PDF upload & CV generation — only for existing recruiters */}
                    {editing !== 'new' && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Document PDF / CV généré</label>
                            {recruiters.find(r => r.id === editing)?.attached_pdf_url ? (
                                <div className="flex items-center gap-4 flex-wrap">
                                    <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm">
                                        <FileText className="w-4 h-4" />
                                        PDF attaché
                                    </div>
                                    <button onClick={() => handleGenerateCv(editing)}
                                        disabled={generatingCv}
                                        className="flex items-center gap-1.5 text-sm bg-[#5764F4] hover:bg-[#4654E3] text-white px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        {generatingCv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                        {generatingCv ? 'Génération...' : 'Régénérer le CV'}
                                    </button>
                                    <button onClick={() => handleDeletePdf(editing)}
                                        className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 transition-colors">
                                        <X className="w-3.5 h-3.5" /> Supprimer
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 flex-wrap">
                                    <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden"
                                        onChange={(e) => handlePdfUpload(editing, e.target.files[0])} />
                                    <button onClick={() => pdfInputRef.current?.click()}
                                        disabled={uploadingPdf === editing}
                                        className="flex items-center gap-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        <Upload className="w-4 h-4" />
                                        {uploadingPdf === editing ? 'Upload en cours...' : 'Uploader un PDF'}
                                    </button>
                                    <button onClick={() => handleGenerateCv(editing)}
                                        disabled={generatingCv}
                                        className="flex items-center gap-2 text-sm bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        {generatingCv ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                        {generatingCv ? 'Génération en cours...' : 'Générer un CV adapté'}
                                    </button>
                                    <p className="w-full text-xs text-gray-400 mt-1">Uploadez un PDF ou générez un CV adapté au poste avec l'IA — Max 30 Mo pour l'upload</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Recruiters list */}
            <div className="space-y-3">
                {recruiters.length === 0 && editing === null && (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Aucun recruteur</p>
                        <p className="text-gray-400 text-sm mt-1">
                            Ajoutez un recruteur pour générer un lien de chat personnalisé
                        </p>
                    </div>
                )}

                {recruiters.map((r) => (
                    <div key={r.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-base font-semibold text-gray-900">{r.recruiter_name}</h3>
                                    {r.company_name && (
                                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{r.company_name}</span>
                                    )}
                                </div>
                                {r.job_title && <p className="text-sm text-gray-500 mt-0.5">{r.job_title}</p>}
                                {r.recruiter_role && <p className="text-xs text-gray-400 mt-1">{r.recruiter_role}</p>}

                                {/* Link */}
                                {r.slug && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 text-xs font-mono text-gray-600">
                                            <Link className="w-3 h-3" />
                                            {window.location.origin}/r/{r.slug}
                                        </div>
                                        <button onClick={() => copyLink(r.slug)}
                                            className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg transition-colors ${copiedId === r.slug ? 'bg-green-100 text-green-700' : 'bg-indigo-50 text-[#5764F4] hover:bg-indigo-100'
                                                }`}>
                                            {copiedId === r.slug ? (
                                                <><CheckCircle className="w-3 h-3" /> Copié</>
                                            ) : (
                                                <><Copy className="w-3 h-3" /> Copier</>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Stats */}
                                <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                                    <span>{r.is_active ? '● Actif' : '○ Inactif'}</span>
                                    {r.intro_video_url && (
                                        <span className="flex items-center gap-1 text-green-600">
                                            <Video className="w-3 h-3" /> Vidéo
                                        </span>
                                    )}
                                    {r.attached_pdf_url && (
                                        <span className="flex items-center gap-1 text-blue-600">
                                            <FileText className="w-3 h-3" /> PDF
                                        </span>
                                    )}
                                    <span>Créé : {formatDate(r.created_at)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                                <button onClick={() => openEdit(r)} className="p-2 text-gray-400 hover:text-[#5764F4] hover:bg-indigo-50 rounded-lg transition-colors">
                                    <Edit3 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(r.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
